import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, PackageSearch } from "lucide-react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { StageBadge } from "@/components/rastreo/StageBadge";
import { TipoIcon } from "@/components/rastreo/TipoIcon";
import { portalApi } from "@/lib/portalAuth";
import { mediaUrl } from "@/lib/api";
import { useDebounced } from "@/lib/useDebounced";
import { formatFecha, nombreTipo, iconoTipo } from "@/lib/format";
import { PORTAL } from "@/constants/testIds";
import { usePageTitle } from "@/lib/usePageTitle";
import { Skeleton } from "@/components/ui/skeleton";
import { ErrorState } from "@/components/common/ErrorState";

export default function PortalUnidades() {
  usePageTitle("Mis unidades");
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [etapaId, setEtapaId] = useState("");
  const [tipo, setTipo] = useState("");
  const qDebounced = useDebounced(q, 300);

  const { data: etapas = [] } = useQuery({
    queryKey: ["etapas"],
    queryFn: () => portalApi.get("/etapas").then((r) => r.data),
  });
  const { data: tipos = [] } = useQuery({
    queryKey: ["tipos"],
    queryFn: () => portalApi.get("/tipos").then((r) => r.data),
  });


  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["portal-unidades", qDebounced, etapaId, tipo],
    queryFn: () =>
      portalApi
        .get("/portal/unidades", { params: { q: qDebounced || undefined, etapa_id: etapaId || undefined, tipo: tipo || undefined } })
        .then((r) => r.data),
    placeholderData: keepPreviousData,
  });

  return (
    <div className="mx-auto max-w-5xl px-5 py-8">
      <h1 className="text-xl font-semibold text-[var(--tc-text)]">Mis unidades</h1>
      <p className="mt-1 text-sm text-[var(--tc-text-secondary)]">Seguí en tiempo real el estado de la importación de tus unidades.</p>

      {data && (
        <div className="mt-5 grid grid-cols-3 gap-3">
          <ResumenCard testId={PORTAL.summaryTotal} label="Total" valor={data.resumen.total} />
          <ResumenCard testId={PORTAL.summaryEnCurso} label="En curso" valor={data.resumen.en_curso} acento />
          <ResumenCard testId={PORTAL.summaryEntregadas} label="Entregadas" valor={data.resumen.entregadas} />
        </div>
      )}

      <div className="mt-6 flex flex-wrap gap-2.5">
        <div className="relative flex-1 sm:flex-none">
          <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--tc-text-secondary)]" />
          <input
            data-testid={PORTAL.searchInput}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por VIN, pedido o embarque"
            className="w-full rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white py-2 pl-8 pr-3 text-sm outline-none focus:border-[var(--tc-accent)] sm:w-52"
          />
        </div>
        <select
          data-testid={PORTAL.filterEtapa}
          value={etapaId}
          onChange={(e) => setEtapaId(e.target.value)}
          className="rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white px-3 py-2 text-sm text-[var(--tc-text)] outline-none focus:border-[var(--tc-accent)]"
        >
          <option value="">Todas las etapas</option>
          {etapas.map((e) => (
            <option key={e.id} value={e.id}>{e.nombre}</option>
          ))}
        </select>
        <select
          data-testid={PORTAL.filterTipo}
          value={tipo}
          onChange={(e) => setTipo(e.target.value)}
          className="rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white px-3 py-2 text-sm text-[var(--tc-text)] outline-none focus:border-[var(--tc-accent)]"
        >
          <option value="">Todos los tipos</option>
          {tipos.map((t) => (
            <option key={t.id} value={t.id}>{t.nombre}</option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="portal-unidades-skeleton">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="overflow-hidden rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white">
              <Skeleton className="aspect-[4/3] w-full rounded-none" />
              <div className="space-y-2 p-4">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            </div>
          ))}
        </div>
      ) : isError ? (
        <div className="mt-6 rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white">
          <ErrorState testId="portal-unidades-error" mensaje="No se pudo cargar tu listado de unidades." onRetry={refetch} />
        </div>
      ) : !data || data.resultados.length === 0 ? (
        <div data-testid={PORTAL.emptyState} className="mt-6 flex flex-col items-center rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white py-16 text-center">
          <PackageSearch size={26} className="text-[var(--tc-state-pending)]" />
          <p className="mt-3 text-sm text-[var(--tc-text-secondary)]">No hay unidades con esos filtros.</p>
        </div>
      ) : (
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {data.resultados.map((u) => (
            <button
              key={u.id}
              data-testid={PORTAL.unidadCard}
              onClick={() => navigate(`/portal/unidad/${u.vin}`)}
              className="group flex flex-col overflow-hidden rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white text-left transition-colors hover:border-[var(--tc-accent)]"
            >
              <div className="aspect-[4/3] w-full overflow-hidden bg-[var(--tc-surface)]">
                {u.foto_principal ? (
                  <img
                    src={mediaUrl(u.foto_principal.url)}
                    alt={`${u.marca} ${u.modelo}`}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-[var(--tc-state-pending)]">
                    <TipoIcon icono={iconoTipo(tipos, u.tipo)} size={28} />
                  </div>
                )}
              </div>
              <div className="flex flex-1 flex-col gap-2 p-4">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-semibold text-[var(--tc-text)]">{u.marca} {u.modelo}</p>
                    <p className="mt-0.5 font-mono text-xs tabular-nums text-[var(--tc-text-secondary)]">{u.vin}</p>
                  </div>
                  <StageBadge etapa={u.etapa_actual} />
                </div>
                <div className="mt-auto flex items-center justify-between text-xs tabular-nums text-[var(--tc-text-secondary)]">
                  <span>{nombreTipo(tipos, u.tipo)}</span>
                  {u.fecha_estimada_entrega && (
                    <span>Entrega est.: {formatFecha(u.fecha_estimada_entrega)}</span>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ResumenCard({ label, valor, testId, acento }) {
  return (
    <div data-testid={testId} className="rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white p-4 text-center">
      <p className={`text-2xl font-semibold ${acento ? "text-[var(--tc-accent)]" : "text-[var(--tc-text)]"}`}>{valor}</p>
      <p className="mt-1 text-xs text-[var(--tc-text-secondary)]">{label}</p>
    </div>
  );
}
